import { Injectable } from '@angular/core';

interface StoredLoginReturnTarget {
  url: string;
  savedAt: number; // milliseconds since 1970
}

/**
 * Remembers the page a visitor was trying to reach when they were asked to log in, so that
 * they can be taken there once they have authenticated.
 *
 * The target is kept in localStorage, because registration leaves the tab (the visitor
 * verifies their email address from a link) and LinkedIn login completes in a popup.
 * A target older than {@link LoginReturnTargetService.maxAgeMilliseconds} is discarded, so
 * that a link followed and abandoned earlier does not redirect a later, unrelated login.
 */
@Injectable({ providedIn: 'root' })
export class LoginReturnTargetService {
  static readonly storageKey = 'loginReturnTarget';
  static readonly maxAgeMilliseconds = 60 * 60 * 1000;

  // Pages that are part of logging in or registering; returning to them after login is pointless
  private static readonly authenticationPaths = [
    '/login',
    '/register',
    '/forgot-password',
    '/password/reset',
    '/email/verify',
    '/oauth',
  ];

  /**
   * @param url an application URL as given by Router.url: path, query and fragment
   */
  remember(url: string | null | undefined): void {
    if (!this.isAcceptableTarget(url)) {
      return;
    }
    const target: StoredLoginReturnTarget = { url, savedAt: Date.now() };
    try {
      localStorage.setItem(LoginReturnTargetService.storageKey, JSON.stringify(target));
    } catch {
      // Storage unavailable (private mode, quota): the visitor lands on the default page instead
    }
  }

  /** The remembered URL, if any and not expired, without clearing it */
  peek(): string | null {
    const target = this.read();
    if (!target) {
      return null;
    }
    if (Date.now() - target.savedAt > LoginReturnTargetService.maxAgeMilliseconds || !this.isAcceptableTarget(target.url)) {
      this.clear();
      return null;
    }
    return target.url;
  }

  /** The remembered URL, if any and not expired; clears it so that it is used only once */
  take(): string | null {
    const url = this.peek();
    this.clear();
    return url;
  }

  clear(): void {
    try {
      localStorage.removeItem(LoginReturnTargetService.storageKey);
    } catch {
      // Nothing to clear
    }
  }

  private read(): StoredLoginReturnTarget | null {
    try {
      const raw = localStorage.getItem(LoginReturnTargetService.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<StoredLoginReturnTarget>;
      if (typeof parsed?.url !== 'string' || typeof parsed?.savedAt !== 'number') {
        return null;
      }
      return { url: parsed.url, savedAt: parsed.savedAt };
    } catch {
      return null;
    }
  }

  /** Only a path inside this application: never another site, never an authentication page */
  private isAcceptableTarget(url: string | null | undefined): url is string {
    if (!url || !url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
      return false;
    }
    const path = url.split(/[?#]/)[0];
    if (path === '/') {
      return false;
    }
    return !LoginReturnTargetService.authenticationPaths.some(
      (authenticationPath) => path === authenticationPath || path.startsWith(authenticationPath + '/'),
    );
  }
}
